import {
  type Geofence,
  type PreparedRanksPackage,
  type TagsCollection,
  eventHasShadowtrack,
  testEventHasVirtDistance,
  testEventHasVirtDuration,
} from '@racemap/utilities/functions/timing';
import { isRunningEvent, isStageGroupEvent } from '@racemap/utilities/functions/utils';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import type { Splits } from '@racemap/utilities/types/types';
import type { Immutable } from 'immer';
import type { EventData } from '../../../store/leaderboard/leaderboard_reducer';
import { getActiveDurationCol } from './Columns/ActiveDurationCol';
import { getProgressCol } from './Columns/CurrentProgressCol';
import { getDifferenceCol } from './Columns/DifferenceCol';
import { getLiveStateCol } from './Columns/LiveStateCol';
import { getMapLinkCol } from './Columns/MapLinkCol';
import { getNameCol } from './Columns/NameCol';
import { rankCol } from './Columns/RankCol';
import { getRestDurationCol } from './Columns/RestDurationCol';
import { getStageProgressCol } from './Columns/StageProgressCol';
import { starterNumberCol } from './Columns/StartNumberCol';
import { getTagColumns } from './Columns/TagCol';
import { getTimesCol } from './Columns/TimesCol';
import { getTimesFinishCol } from './Columns/TimesFinishCol';
import { getStartTimesCol } from './Columns/TimesStartCol';
import type { Column } from './TableBody';

export const getFixedColumnCount = (event: Immutable<RacemapEvent>, windowWith: number): number => {
  const visibleElements = event.modules.timing.visibleElements;
  const eventIsRunning = isRunningEvent(event);
  const displayLiveState = visibleElements.liveState && eventIsRunning;

  if (windowWith < 660) return 0;
  if (isStageGroupEvent(event)) {
    if (displayLiveState) return 3;
    return 2;
  }
  if (displayLiveState) return 4;

  return 3;
};

export function getColumnsForStageGroupEvent(
  stageGroupEvent: Immutable<RacemapEvent>,
  tags: Immutable<TagsCollection>,
  eventsData: Immutable<Map<string, Immutable<EventData>>>,
  windowWidth: number,
): Array<Column> {
  const columns: Array<Column> = [];
  const visibleElements = stageGroupEvent.modules.timing.visibleElements;
  const eventIsRunning = isRunningEvent(stageGroupEvent);
  const displayLiveState = visibleElements.liveState && eventIsRunning;

  if (displayLiveState) columns.push(getLiveStateCol());
  columns.push(starterNumberCol);
  columns.push(getNameCol(windowWidth));
  columns.push(...getTagColumns(tags, stageGroupEvent));

  for (const eventData of Array.from(eventsData.values())) {
    // skip the selected Stage Group Event
    if (stageGroupEvent.id === eventData.eventObject.id) continue;

    columns.push(
      getStageProgressCol(eventData.eventObject, eventData.splits, eventData.starterData),
    );
  }

  return columns;
}

export const getColumnsForAtomicEvent = (
  event: Immutable<RacemapEvent>,
  splits: Immutable<Splits>,
  eventData: Immutable<PreparedRanksPackage>,
  timekeepings: Immutable<Array<Geofence>>,
  tags: Immutable<TagsCollection>,
  windowWidth: number,
  showStartTime: boolean,
  showDeltaTime: boolean,
  embedded: boolean,
) => {
  const columns: Array<Column> = [];
  const withShadowtrack = eventHasShadowtrack(event);
  const eventHasVirtDuration = testEventHasVirtDuration(event);
  const eventHasVirtDistance = testEventHasVirtDistance(event);
  const visibleElements = event.modules.timing.visibleElements;
  const eventIsRunning = isRunningEvent(event);
  const displayLiveState = visibleElements.liveState && eventIsRunning;

  const displayCurrentProgress =
    (visibleElements.currentProgress && eventHasShadowtrack) ||
    ((eventHasVirtDistance || eventHasVirtDuration) &&
      (!withShadowtrack || event.modules.projection.enabled));

  if (displayLiveState) columns.push(getLiveStateCol());
  if (visibleElements.rank) columns.push(rankCol(event, timekeepings));
  columns.push(starterNumberCol);
  columns.push(getNameCol(windowWidth));
  columns.push(...getTagColumns(tags, event, eventData.tagsStarterCount));
  if (displayCurrentProgress) columns.push(getProgressCol(event, splits, windowWidth));
  if (visibleElements.finish)
    columns.push(getTimesFinishCol(event, splits, timekeepings, windowWidth));
  if (visibleElements.difference)
    columns.push(getDifferenceCol(event, timekeepings, windowWidth, eventData));
  if (visibleElements.activeDuration)
    columns.push(getActiveDurationCol(event, timekeepings, windowWidth));
  if (visibleElements.restDuration)
    columns.push(getRestDurationCol(event, timekeepings, windowWidth));
  if (showStartTime) columns.push(getStartTimesCol(event, splits, timekeepings));
  if (visibleElements.stages) {
    for (let i = 1; i < timekeepings.length - 1; i++) {
      columns.push(getTimesCol(event, i, timekeepings, splits, windowWidth, showDeltaTime));
    }
  }
  if (visibleElements.mapLink && !embedded) columns.push(getMapLinkCol(event));

  return columns;
};
